import { get } from 'lodash';
import { Modal, Spinner } from 'flowbite-react';
import React, { useEffect, useRef, useState } from 'react';
import { HiOutlineArrowLeft, HiOutlineArrowRight, HiOutlineMail, HiOutlinePhone, HiOutlineUser, HiX } from 'react-icons/hi';
import { useMutation } from 'react-query';
import { Drawer } from 'vaul';
import { guestDriveSendOtp, guestDriveVerifyOtp } from 'src/apis/drive';
import { appBasePath, PrimaryButton, useIsMobile } from './shared';
import { useToast } from 'src/context/ToastProvider';

// Six separate digit boxes: auto-advance, backspace goes back, pasting a full code fills them all.
const OtpBoxes = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
	const refs = useRef<Array<HTMLInputElement | null>>([]);
	const digits = Array.from({ length: 6 }, (_, i) => value[i] ?? '');
	const setAt = (index: number, char: string) => {
		const next = digits.slice();
		next[index] = char;
		onChange(next.join('').slice(0, 6));
	};
	return (
		<div className="flex justify-center gap-2 md:gap-3">
			{digits.map((digit, index) => (
				<input
					key={index}
					ref={el => (refs.current[index] = el)}
					autoFocus={index === 0}
					inputMode="numeric"
					autoComplete={index === 0 ? 'one-time-code' : 'off'}
					maxLength={1}
					value={digit}
					aria-label={`Digit ${index + 1}`}
					onChange={e => {
						const char = e.target.value.replace(/\D/g, '').slice(-1);
						setAt(index, char);
						if (char && index < 5) refs.current[index + 1]?.focus();
					}}
					onKeyDown={e => {
						if (e.key === 'Backspace' && !digits[index] && index > 0) refs.current[index - 1]?.focus();
					}}
					onPaste={e => {
						const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
						if (!pasted) return;
						e.preventDefault();
						onChange(pasted);
						refs.current[Math.min(pasted.length, 5)]?.focus();
					}}
					className={`h-[54px] min-w-0 flex-1 md:h-[64px] md:max-w-[56px] rounded-[14px] border-[1.5px] text-center text-[22px] md:text-[26px] font-[700] text-[#0F1626] outline-none transition-all focus:border-[#4F46E5] focus:bg-white focus:ring-4 focus:ring-[#DCD9FA] ${
						digit ? 'border-[#B8B3F7] bg-[#EEEDFD]' : 'border-transparent bg-[#EDEDF4]'
					}`}
				/>
			))}
		</div>
	);
};

// Input with a leading icon, like the reference auth designs.
const Field = ({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) => (
	<label className="block text-[13px] font-[700] text-[#0F1626]">
		{label}
		<span className="mt-1.5 flex items-center gap-3 rounded-[14px] border-[1.5px] border-[#E3E3EC] bg-white px-4 transition-all focus-within:border-[#4F46E5] focus-within:ring-4 focus-within:ring-[#EEEDFD]">
			<span className="text-[#8A8BA3]">{icon}</span>
			{children}
		</span>
	</label>
);
const inputClass = 'w-full bg-transparent py-3.5 text-[15px] font-[400] text-[#0F1626] !border-0 !shadow-none !outline-none !ring-0 focus:!ring-0 placeholder:text-[#9A9BB0]';


// Guest onboarding in place: details, then the email OTP. On success the guest is signed in with a full page load
// (the app reads the token from the URL) and lands on the resume step. Modal on desktop, Vaul bottom sheet on mobile.
const GuestSignInModal = ({ open, onClose, driveId, mode = 'register' }: { open: boolean; onClose: () => void; driveId: string; mode?: 'register' | 'login' }) => {
	const isMobile = useIsMobile();
	const toast = useToast();
	const [step, setStep] = useState<'details' | 'otp'>('details');
	const [form, setForm] = useState({ name: '', email: '', phone: '', otp: '' });
	const [resendIn, setResendIn] = useState(0);
	const error = (e: unknown) => toast({ type: 'error', title: get(e, ['response', 'data', 'message'], (e as Error).message) });

	useEffect(() => {
		if (resendIn <= 0) return;
		const timer = setTimeout(() => setResendIn(resendIn - 1), 1000);
		return () => clearTimeout(timer);
	}, [resendIn]);

	useEffect(() => {
		if (!open) {
			setStep('details');
			setForm(prev => ({ ...prev, otp: '' }));
		}
	}, [open]);

	const sendOtp = useMutation(guestDriveSendOtp, {
		onSuccess() {
			setResendIn(30);
			toast({ type: 'success', title: `We sent a 6 digit code to ${form.email}` });
			setStep('otp');
		},
		onError: error,
	});
	const verifyOtp = useMutation(guestDriveVerifyOtp, {
		onSuccess(result) {
			window.location.assign(`${window.location.origin}${appBasePath()}/placement-drives/${driveId}/resume?token=${encodeURIComponent(result.token)}`);
		},
		onError: error,
	});

	const canSend = !!form.email && (mode === 'login' || (form.name.trim().length >= 2 && /^[0-9]{10}$/.test(form.phone.trim())));
	const send = () => sendOtp.mutate({ email: form.email.trim(), name: form.name, phone: form.phone, driveId, mode });

	const body = (
		<form
			className="flex flex-col gap-5"
			onSubmit={e => {
				e.preventDefault();
				if (step === 'details') send();
				else verifyOtp.mutate({ email: form.email.trim(), otp: form.otp.trim(), driveId, mode });
			}}
		>
			{step === 'details' ? (
				<>
					{mode === 'register' ? (
						<>
							<Field label="Full name" icon={<HiOutlineUser className="h-5 w-5" />}>
								<input className={inputClass} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
							</Field>
							<Field label="Phone number" icon={<HiOutlinePhone className="h-5 w-5" />}>
								<input className={inputClass} inputMode="numeric" maxLength={10} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value.replace(/\D/g, '') })} placeholder="10 digit mobile number" />
							</Field>
						</>
					) : null}
					<Field label="Email" icon={<HiOutlineMail className="h-5 w-5" />}>
						<input type="email" className={inputClass} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
					</Field>
					<PrimaryButton type="submit" className="mt-1 w-full !py-4 !text-[16px]" disabled={!canSend || sendOtp.isLoading}>
						{sendOtp.isLoading ? (
							<Spinner size="sm" light />
						) : (
							<span className="inline-flex items-center gap-2">
								Send code <HiOutlineArrowRight className="h-5 w-5" />
							</span>
						)}
					</PrimaryButton>
				</>
			) : (
				<>
					<OtpBoxes value={form.otp} onChange={otp => setForm({ ...form, otp })} />
					<PrimaryButton type="submit" className="w-full !py-4 !text-[16px]" disabled={form.otp.length < 6 || verifyOtp.isLoading}>
						{verifyOtp.isLoading ? (
							<Spinner size="sm" light />
						) : (
							<span className="inline-flex items-center gap-2">
								Verify and continue <HiOutlineArrowRight className="h-5 w-5" />
							</span>
						)}
					</PrimaryButton>
					<div className="flex flex-col items-center gap-1.5 border-t border-[#EEEEF5] pt-4 text-[14px]">
						{resendIn > 0 ? (
							<span className="font-[500] text-[#8A8BA3]">Resend code in 0:{String(resendIn).padStart(2, '0')}</span>
						) : (
							<button type="button" className="font-[700] text-[#4F46E5] disabled:opacity-50" disabled={sendOtp.isLoading} onClick={send}>
								Resend code
							</button>
						)}
						<button type="button" className="font-[600] text-[#555770] hover:text-[#0F1626]" onClick={() => setStep('details')}>
							Wrong email? Change it
						</button>
					</div>
				</>
			)}
		</form>
	);

	const title = mode === 'login' ? 'Guest sign in' : step === 'details' ? 'Join as a guest' : 'Enter the code';
	const subtitle =
		step === 'details' ? (
			mode === 'login' ? 'Use the email you registered with at the drive.' : 'No Masai account? Tell us a bit about you to apply to the drive.'
		) : (
			<>
				We sent a 6 digit code to <b className="font-[700] text-[#0F1626]">{form.email}</b>
			</>
		);

	const header = (
		<div className="flex items-start gap-3.5">
			{step === 'otp' ? (
				<button aria-label="Back" onClick={() => setStep('details')} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F1F1F8] text-[#3A3B52] hover:bg-[#E6E6F4]">
					<HiOutlineArrowLeft className="h-[18px] w-[18px]" />
				</button>
			) : (
				<span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-[#EEEDFD] text-[#4F46E5]">
					<HiOutlineUser className="h-5 w-5" />
				</span>
			)}
			<div className="min-w-0 flex-1">
				<h3 className="text-[22px] leading-[28px] font-[800] tracking-[-0.03em] text-[#0F1626]">{title}</h3>
				<p className="mt-0.5 text-[14px] leading-[20px] text-[#555770]">{subtitle}</p>
			</div>
			<button aria-label="Close" onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F1F1F8] text-[#3A3B52] hover:bg-[#E6E6F4]">
				<HiX className="h-[18px] w-[18px]" />
			</button>
		</div>
	);

	if (isMobile) {
		return (
			<Drawer.Root open={open} onOpenChange={next => !next && onClose()}>
				<Drawer.Portal>
					<Drawer.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]" />
					<Drawer.Content className="fixed inset-x-0 bottom-0 z-50 flex max-h-[94vh] flex-col rounded-t-[28px] bg-white outline-none" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
						<Drawer.Title className="sr-only">{title}</Drawer.Title>
						<Drawer.Description className="sr-only">Guest sign in for the drive</Drawer.Description>
						<div className="mx-auto mt-3 h-1.5 w-[44px] shrink-0 rounded-full bg-[#E3E3EC]" />
						<div className="px-5 pt-4">{header}</div>
						<div className="flex-1 overflow-y-auto px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-5" data-vaul-no-drag>
							{body}
						</div>
					</Drawer.Content>
				</Drawer.Portal>
			</Drawer.Root>
		);
	}

	return (
		<Modal show={open} onClose={onClose} size="md" dismissible>
			<div className="p-6" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
				{header}
				<div className="mt-6">{body}</div>
			</div>
		</Modal>
	);
};

export default GuestSignInModal;
